import { useEffect, useRef, useState } from 'react';
import { useSocket } from 'socket.io-react-hook';
import { useQueryClient } from 'react-query';
import * as Sentry from '@sentry/react';
import { _getToken } from 'src/Config/axiosApi';
import { refreshAuthToken } from 'src/Common/context/AuthContext';
import {
  OPENER_DESCALCE_KEY,
  isUsableDescalceSnapshot,
  type OpenerDescalceSnapshot,
} from '../api/useOpenerDescalce';

/** The gateway's namespace on `opener-orchestrator` (frozen with the server). */
export const DESCALCE_NAMESPACE = '/descalce';

/** Event carrying a full snapshot — seed and live broadcast alike. */
export const DESCALCE_SNAPSHOT_EVENT = 'descalce:snapshot';

/** Server-side "there is no snapshot to send you", the WS twin of the REST 404. */
export const DESCALCE_UNAVAILABLE_EVENT = 'descalce:unavailable';

/**
 * MONOTONIC clock, same discipline as `descalceFreshness.ts`: a wall-clock
 * correction (NTP, a VM resume) can jump backwards, and `now - lastFrameAt`
 * going negative latches `pushActive` ON — leaving the REST safety net on its
 * slow cadence at the exact moment it may be the only transport left. That is
 * the unsafe direction, so it does not get to depend on the wall clock.
 */
function monotonicNow(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

/** First delay of our OWN reconnect ladder, and its ceiling. */
const RECONNECT_BASE_MS = 2_000;
export const RECONNECT_MAX_MS = 30_000;

/** Refresh the JWT this many seconds BEFORE it expires. */
const PROACTIVE_REFRESH_LEAD_SECONDS = 60;

/** Never re-arm the proactive refresh faster than this. */
const PROACTIVE_REFRESH_FLOOR_MS = 5_000;

/** Seconds until a JWT's `exp`, or null if it can't be decoded. Local copy —
 *  `AuthContext`'s helper is not exported. Only reads `exp`. */
function secondsUntilTokenExpiry(token: string | null): number | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    ) as { exp?: number };
    if (!payload?.exp) return null;
    return payload.exp - Math.floor(Date.now() / 1000);
  } catch {
    return null;
  }
}

/** Cooldown between token refreshes triggered by a connect/auth error.
 *  Measured on the MONOTONIC clock, same as everything else here. */
const AUTH_RECOVERY_COOLDOWN_MS = 10_000;

/**
 * How long after the last pushed frame the socket still counts as delivering.
 *
 * The FLOOR of the window; the real one is two of the producer's own sweeps,
 * read off `pollMs` on the wire. It has to outlast a single skipped sweep —
 * the sweeper legitimately misses ticks when a venue read runs long — without
 * outlasting a genuinely dead producer, because this is what decides whether
 * the REST safety net goes back to its fast cadence, and that net is the only
 * transport left in exactly that case.
 */
export const PUSH_ACTIVE_WINDOW_FLOOR_MS = 30_000;

/** Only used before the first frame names the real one. */
export const DEFAULT_PRODUCER_POLL_MS = 15_000;

/** How often `pushActive` is re-evaluated once frames have started arriving. */
export const PUSH_ACTIVE_TICK_MS = 5_000;

/**
 * Resolve the namespaced socket URL. The descalce feed is served by the pod
 * that already serves its REST read — `opener-orchestrator` — on the
 * `/descalce` socket.io namespace, so we append the namespace to that same
 * base URL. Falling back to `VITE_PREDICTION_MANAGER_URL` mirrors
 * `openerOrchestratorAxiosApi`'s own transitional fallback, so the socket and
 * the REST read can never end up pointed at two different pods.
 */
export function resolveDescalceWsUrl(): string {
  const base =
    import.meta.env.VITE_OPENER_ORCHESTRATOR_URL ||
    import.meta.env.VITE_PREDICTION_MANAGER_URL ||
    import.meta.env.VITE_PREDICTION_PAIR_MANAGER_URL ||
    '';
  if (!base) return '';
  return `${base.replace(/\/+$/, '')}${DESCALCE_NAMESPACE}`;
}

export interface DescalceSocketApi {
  /** A frame arrived within two of the producer's own sweeps. */
  pushActive: boolean;
}

/**
 * Push transport for the Descalces screen.
 *
 * The sweeper publishes one snapshot per sweep and `opener-orchestrator`
 * broadcasts it to this namespace; every frame is written straight into the
 * react-query cache under {@link OPENER_DESCALCE_KEY}. That is the whole
 * integration: `useOpenerDescalce` keeps owning the data, the page keeps
 * reading it from the same place, and nothing renders from a second source.
 *
 * 🚨 **The REST read is not replaced, and must not be.** Pub/sub retains
 * nothing and delivers nothing while the gateway pod is rolling, so the
 * on-demand read stays the recovery path — see the `pushActive`-driven
 * cadence in `useOpenerDescalce`. On this screen an empty table is a claim
 * ("no naked legs"), so a transport that can go quiet without saying so has to
 * have a second one behind it.
 *
 * `descalce:unavailable` does NOT touch the cache — it only asks the REST read
 * to confirm, so the page's "snapshot unavailable" state keeps exactly one
 * producer (the 404). See the comment on `onUnavailable` for why clearing the
 * cache there makes the screen claim the opposite of what it knows.
 */
export function useOpenerDescalceSocket(): DescalceSocketApi {
  const wsUrl = resolveDescalceWsUrl();
  const accessToken = _getToken();
  const queryClient = useQueryClient();

  const { socket } = useSocket(wsUrl || '', {
    enabled: !!wsUrl && !!accessToken,
    transports: ['websocket'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 30_000,
    auth: (cb: (data: { token: string }) => void) => cb({ token: _getToken() }),
  });

  // `null`, never `0`, for "no frame yet": `monotonicNow()` legitimately
  // returns 0 at the start of a document's life, so a numeric sentinel makes
  // the first frame after a fresh load indistinguishable from no frame — which
  // would leave `pushActive` false and never start the expiry ticker.
  const [lastFrameAt, setLastFrameAt] = useState<number | null>(null);
  /** The producer's own sweep cadence, taken off the last frame. */
  const lastPollMs = useRef<number | null>(null);
  /** A frame has arrived on THIS socket — the handshake is not just accepted
   *  but working. Resets the reconnect ladder; `connect` does not. */
  const connectionProven = useRef(false);
  /** One-shot latch for the malformed-frame report. */
  const malformedReported = useRef(false);
  /** Last PROACTIVE refresh, so its own success cannot re-arm it in a loop. */
  const lastProactiveRefreshAt = useRef(Number.NEGATIVE_INFINITY);
  // `-Infinity`, never `0`: the cooldown is measured on the MONOTONIC clock,
  // which starts near zero at the document's time-origin. A `0` sentinel puts
  // the first auth error of the page's life INSIDE the cooldown — it returns
  // before refreshing and before stamping the ref, so the one path this
  // recovery exists for is the one it skips. (`useNotificationsSocket` gets
  // away with `useRef(0)` because it compares against `Date.now()`, which is
  // always ≫ the cooldown. Switching clocks without switching the sentinel is
  // the bug.) Same shape as `lastFrameAt` four fields up.
  const lastAuthRecoveryAt = useRef(Number.NEGATIVE_INFINITY);

  // ── Frame intake ────────────────────────────────────────────────────
  //
  // Each frame is a COMPLETE snapshot that supersedes the previous one, so it
  // goes straight into the cache slot — there is nothing to accumulate and no
  // batch to lose. (The "never push a batch through a state slot" rule is
  // about N events collapsing into one setState; here N frames legitimately
  // collapse to the newest.)
  useEffect(() => {
    if (!socket) return undefined;

    const onSnapshot = (snapshot: OpenerDescalceSnapshot) => {
      // The SAME gate the REST path applies — see `isUsableDescalceSnapshot`
      // for why each field is load-bearing. Two transports validating
      // differently is how one of them ends up rendering what the other
      // refused.
      if (!isUsableDescalceSnapshot(snapshot)) {
        // Reported ONCE per session: a producer/consumer contract mismatch is
        // one fact, and at one frame per sweep it would otherwise be four
        // Sentry events a minute per tab, forever.
        if (!malformedReported.current) {
          malformedReported.current = true;
          Sentry.captureMessage('descalce WS frame malformed', {
            tags: { area: 'descalce-ws' },
          });
        }
        return;
      }
      // Monotonic on `computedAt`, never a bare write. The push does NOT
      // cancel a REST fetch already in flight, so an older response can land
      // after a newer frame and walk the snapshot backwards — rows that just
      // appeared vanish until the next sweep. `refetchOnWindowFocus` makes
      // that race ordinary: the operator returns to the tab, a refetch starts,
      // a frame arrives, then the refetch resolves. (`useOpenerDescalce`
      // carries the mirror guard for the REST side.)
      queryClient.setQueryData<OpenerDescalceSnapshot | undefined>(
        OPENER_DESCALCE_KEY,
        prev =>
          prev && prev.computedAt > snapshot.computedAt ? prev : snapshot
      );
      // The producer's OWN cadence, off the wire. Hardcoding a window against
      // an assumed 15s sweep breaks as soon as `OPENER_DESCALCE_POLL_MS` moves
      // — it is configurable up to 300s, and past 30s `pushActive` would
      // oscillate every cycle (frame → true → REST to 60s → window expires →
      // false → REST to 5s → next frame), so "safety net vs sole transport"
      // would stop meaning anything.
      lastPollMs.current = snapshot.pollMs;
      setLastFrameAt(monotonicNow());
      // A frame IS a proven connection — the first thing the gateway does on a
      // good handshake is seed. See `armRetry` for why `connect` is not.
      connectionProven.current = true;
    };

    const onUnavailable = () => {
      // Ask the REST read to CONFIRM, and change NOTHING else.
      //
      // 🚨 This deliberately does not clear the cache. `setQueryData(key,
      // undefined)` does not evict a react-query v3 query — it dispatches a
      // SUCCESS with `data: undefined` and a fresh `dataUpdatedAt`. The page
      // then reads `isLoading: false, isError: false, data: undefined`, which
      // `resolveDescalceViewState` resolves to `empty`: the screen would
      // announce "No open positions on any opener rule right now" for the
      // whole REST round trip. On a naked-leg screen that sentence is a
      // claim, and the seed fires on EVERY (re)connect — page load, gateway
      // rollout, laptop lid — so a single Redis hiccup behind
      // `seed-read-failed` would blank a table that was showing real exposure.
      //
      // Leaving the last snapshot in place is the safe half of the same
      // information: the operator keeps their last-known-good rows with the
      // page's own staleness banner aging them, and the refetch below settles
      // it.
      //
      // ⚠️ Precisely: with a snapshot already in hand the page KEEPS showing
      // those rows even after the REST 404 — `resolveDescalceViewState` gives
      // `data` priority over `isError` by design, so the named unavailable
      // state is reached only on a COLD load. That is the intended behaviour
      // (last-known-good plus a Stale banner beats a blank screen); this note
      // exists because the previous wording claimed the 404 always renders it.
      //
      // `lastFrameAt` is deliberately NOT stamped: this is not data. Counting
      // it would flip `pushActive` on and back the confirming read off its
      // fast cadence for the next window — slowing down the exact read this
      // event just asked for.
      void queryClient.invalidateQueries(OPENER_DESCALCE_KEY);
    };

    socket.on(DESCALCE_SNAPSHOT_EVENT, onSnapshot);
    socket.on(DESCALCE_UNAVAILABLE_EVENT, onUnavailable);
    return () => {
      socket.off(DESCALCE_SNAPSHOT_EVENT, onSnapshot);
      socket.off(DESCALCE_UNAVAILABLE_EVENT, onUnavailable);
    };
  }, [socket, queryClient]);

  // ── Connection recovery ─────────────────────────────────────────────
  //
  // 🚨 socket.io does NOT own the retry loop here. Both server-side rejection
  // paths — a `CONNECT_ERROR` (which is also what an unknown namespace
  // returns, i.e. every deploy where the gateway has not shipped yet) and the
  // `disconnect(true)` that `authenticateWsClient` issues — call `destroy()`
  // on the socket, which ends the manager's reconnection for it. So a retry
  // has to be armed on EVERY error exit, not only the one that happens to
  // reach the end of the auth branch. Getting that wrong leaves the push
  // transport dead for the rest of the session, silently.
  //
  // A refresh is attempted only for auth-shaped failures: socket.io emits
  // `connect_error` on every failed attempt (pod down, DNS, ALB 502), and
  // refreshing on all of them POSTs `/auth/refresh` on a loop forever.
  useEffect(() => {
    if (!socket) return undefined;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let retryDelayMs = RECONNECT_BASE_MS;

    const armRetry = () => {
      if (retryTimer) return;
      retryTimer = setTimeout(() => {
        retryTimer = null;
        retryDelayMs = Math.min(retryDelayMs * 2, RECONNECT_MAX_MS);
        try {
          socket.connect();
        } catch {
          /* the next error re-arms this */
        }
      }, retryDelayMs);
    };

    const onConnect = () => {
      // Cancel the pending retry, so a timer still in flight cannot fire its
      // no-op `connect()` and double the delay behind our back.
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      // 🚨 But do NOT reset the ladder here. `authenticateWsClient` runs
      // INSIDE `handleConnection`, after socket.io has already acked the
      // CONNECT — so the gateway's real rejection sequence at the browser is
      // `connect` → `auth_error` → `disconnect`. Resetting on `connect` resets
      // on a connection that dies in the same tick, and the ladder stays
      // pinned at its base forever. Measured: a JWT the refresh cannot fix
      // (expired session, rotated `JWT_SECRET`, pod clock skew) produced
      // [2000, 2000, 2000, 2000…] instead of [2000, 4000, 8000, 16000,
      // 30000…] — one handshake every 2s, one `/auth/refresh` every 10s and
      // one Sentry event every 10s, per open tab, indefinitely. A
      // `JWT_SECRET` mismatch hits every operator's tabs at once, and this
      // screen is one people leave open all day.
      //
      // A frame is the proof `connect` is not: the gateway seeds immediately
      // on a good handshake, so receiving one means the connection works.
      if (connectionProven.current) retryDelayMs = RECONNECT_BASE_MS;
    };

    const looksLikeAuth = (err: unknown): boolean => {
      const msg = (
        (err as { message?: string })?.message ??
        (err as { data?: { message?: string } })?.data?.message ??
        ''
      ).toLowerCase();
      return (
        msg.includes('auth') ||
        msg.includes('unauthor') ||
        msg.includes('token') ||
        msg.includes('jwt') ||
        msg.includes('forbidden')
      );
    };

    const handleError = async (err: unknown, fromAuthEvent: boolean) => {
      // This socket has not proven itself (again) — the next `connect` must
      // not reset the ladder on our behalf.
      connectionProven.current = false;
      // FIRST, unconditionally: something must reconnect us.
      armRetry();

      // `auth_error` is ours and always means auth; `connect_error` has to
      // prove it before it gets to spend a token refresh.
      if (!fromAuthEvent && !looksLikeAuth(err)) return;

      const now = monotonicNow();
      if (now - lastAuthRecoveryAt.current < AUTH_RECOVERY_COOLDOWN_MS) return;
      lastAuthRecoveryAt.current = now;

      let newToken: string | null = null;
      // `clearOnFailure: false` — a descalce-WS blip must never wipe the
      // operator's session. `refreshAuthToken` swallows its own failures and
      // resolves `null`, so there is nothing to catch here.
      newToken = await refreshAuthToken({ clearOnFailure: false });

      if (newToken) {
        // Reconnect with the fresh token NOW rather than waiting out the
        // armed retry. No `disconnect()` first: on a socket the server already
        // tore down it is a no-op, and on a live manager it resets the backoff.
        try {
          socket.connect();
        } catch {
          /* the armed retry still covers us */
        }
        return;
      }

      // No toast: this screen keeps reading over REST while the socket is
      // down, so the operator is not losing data.
      Sentry.captureException(
        err instanceof Error ? err : new Error('descalce WS auth error'),
        { tags: { area: 'descalce-ws' } }
      );
    };

    const onConnectError = (err?: unknown) => void handleError(err, false);
    const onAuthError = (err?: unknown) => void handleError(err, true);

    socket.on('connect', onConnect);
    socket.on('connect_error', onConnectError);
    socket.on('auth_error', onAuthError);
    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      socket.off('connect', onConnect);
      socket.off('connect_error', onConnectError);
      socket.off('auth_error', onAuthError);
    };
  }, [socket]);

  // ── Proactive token refresh ─────────────────────────────────────────
  //
  // Refresh before `exp` so the gateway never gets a chance to reject the
  // socket in the first place — the same guard `useNotificationsSocket` has.
  // Without it everything rests on the reactive path above, and the two
  // failure modes compound.
  useEffect(() => {
    const ttl = secondsUntilTokenExpiry(accessToken);
    if (ttl === null) return undefined;

    // 🚨 Rate-limit the PROACTIVE path against itself. Its own success changes
    // the token, which re-runs this effect and re-arms the timer — so any
    // state where the issued token is already near or past `exp` (a backend
    // handing back expired tokens, a browser clock ahead of the token's life)
    // becomes a `/auth/refresh` every floor, per tab, forever. The floor caps
    // the RATE; this is what breaks the CYCLE.
    const sinceLast = monotonicNow() - lastProactiveRefreshAt.current;
    if (ttl <= PROACTIVE_REFRESH_LEAD_SECONDS && sinceLast < PROACTIVE_REFRESH_FLOOR_MS) {
      return undefined;
    }
    // An already-expired token still schedules — bailing outright would leave
    // nothing armed and nothing to re-arm it, since this effect only re-runs
    // when the token string changes. The rate guard above is the exception:
    // past the lead time it DOES bail, because there the reactive path is the
    // backstop and re-arming would be the loop itself.
    //
    // The floor matters as much as the lead: at `delayMs: 0` a backend issuing
    // short-lived tokens turns every success into an immediate re-arm, i.e. a
    // `/auth/refresh` loop at round-trip speed.
    const delayMs = Math.max(
      PROACTIVE_REFRESH_FLOOR_MS,
      (ttl - PROACTIVE_REFRESH_LEAD_SECONDS) * 1000
    );
    const timer = setTimeout(() => {
      lastProactiveRefreshAt.current = monotonicNow();
      refreshAuthToken({ clearOnFailure: false }).catch(err => {
        Sentry.captureException(err, { tags: { area: 'descalce-ws' } });
      });
    }, delayMs);
    return () => clearTimeout(timer);
  }, [accessToken]);

  // ── pushActive: re-evaluated on a timer, not only on a frame ─────────
  //
  // Without the timer, `pushActive` would stay true forever after the LAST
  // frame — the producer dying is precisely the case with no further frames
  // to recompute on, and precisely the case the REST net must speed up for.
  const [now, setNow] = useState(() => monotonicNow());
  useEffect(() => {
    // Only once frames have started arriving: before that `pushActive` is
    // already false and there is nothing for the ticker to change, so it would
    // just re-render the page every 5s for a constant.
    if (lastFrameAt === null) return undefined;
    const id = setInterval(() => setNow(monotonicNow()), PUSH_ACTIVE_TICK_MS);
    return () => clearInterval(id);
  }, [lastFrameAt]);

  // TWO sweeps of whatever cadence the producer reported, floored so a very
  // fast sweep cannot make the window shorter than a single missed tick plus
  // jitter. Derived, not assumed: `OPENER_DESCALCE_POLL_MS` is configurable
  // from 5s to 300s.
  const windowMs = Math.max(
    PUSH_ACTIVE_WINDOW_FLOOR_MS,
    2 * (lastPollMs.current ?? DEFAULT_PRODUCER_POLL_MS)
  );
  const pushActive = lastFrameAt !== null && now - lastFrameAt < windowMs;

  // Deliberately only `pushActive`. A `connected` flag nothing renders is a
  // second notion of liveness that can disagree with the one that drives the
  // REST cadence — and the socket being UP says nothing about the producer
  // being alive, which is what the operator would read it as.
  return { pushActive };
}
